import React from "react";
import styled from "styled-components";

const Container = styled.div`
  margin: 0 auto;
  background: white;
  font-family: "Pretendard", Arial, sans-serif;
  line-height: 1.6;
  color: #333;
  padding: 40px 0px;

  @media print {
    padding: 0;
    max-width: none;
    box-shadow: none;
  }

  .category-section {
    margin-bottom: 30px;
  }

  .category-title {
    background-color: #f8f9fa;
    padding: 12px 16px;
    border-left: 4px solid  #2c3e50;
    font-weight: bold;
    font-size: 16px;
    color: #2c3e50;
    margin-bottom: 12px;
    margin-right: 20px;
  }

  .medication-list {
    list-style: none;
    padding: 0;
    margin: 0;
  }

  .medication-item {
    display: flex;
    background-color: #f8f9fa;
    justify-content: space-between;
    align-items: center;
    padding: 8px 16px;
    margin-bottom: 4px;
    margin-left: 20px;
    margin-right: 20px;
    border-left: 4px solid #e9ecef;
  }

  .medication-item.score-1 {
    border-left: 4px solid #2e7d32;
  }

  .medication-item.score-2 {
    border-left: 4px solid #f57c00;
  }

  .medication-item.score-3 {
    border-left: 4px solid #dc3545;
  }

  .medication-name {
    font-family: "Pretendard";
    font-weight: 400;
    flex: 1;
  }


  .score-badge {
    display: inline-block;
    padding: 4px 8px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: bold;
    min-width: 24px;
    text-align: center;
    color: white;
  }

  .score-badge.score-1 {
    background-color: #2e7d32;
  }

  .score-badge.score-2 {
    background-color: #f57c00;
  }

  .score-badge.score-3 {
    background-color: #dc3545;
  }

  @media screen and (max-width: 768px) {
    .medication-item {
      padding: 6px 12px;
    }
  }
`;

const Title = styled.h1`
  text-align: center;
  color: #2c3e50;
  margin-bottom: 20px;
  font-size: 28px;
  font-weight: bold;
`;

const Description = styled.div`
  background-color: #f8f9fa;
  border-left: 4px solid #007bff;
  padding: 15px;
  margin: 0 20px 30px 0px;
  font-size: 14px;
  line-height: 1.6;
`;

const KABS: React.FC = () => {
  const categories = [
    {
      name: "Analgesics",
      koreanName: "진통제",
      medications: [
        { id: "KABS_Nefopam", name: "Nefopam", score: 2 },
        { id: "KABS_Pethidine", name: "Pethidine", score: 2 },
        { id: "KABS_Tramadol", name: "Tramadol", score: 2 },
        { id: "KABS_Codeine", name: "Codeine", score: 1 },
        { id: "KABS_Fentanyl", name: "Fentanyl", score: 1 },
        { id: "KABS_Hydrocodone", name: "Hydrocodone", score: 1 },
        { id: "KABS_Morphine", name: "Morphine", score: 1 },
        { id: "KABS_Oxycodone", name: "Oxycodone", score: 1 }
      ]
    },
    {
      name: "Antidepressants",
      koreanName: "항우울제",
      medications: [
        { id: "KABS_Amitriptyline", name: "Amitriptyline", score: 3 },
        { id: "KABS_Amoxapine", name: "Amoxapine", score: 3 },
        { id: "KABS_Clomipramine", name: "Clomipramine", score: 3 },
        { id: "KABS_Doxepin", name: "Doxepin", score: 3 },
        { id: "KABS_Imipramine", name: "Imipramine", score: 3 },
        { id: "KABS_Nortriptyline", name: "Nortriptyline", score: 3 },
        { id: "KABS_Paroxetine", name: "Paroxetine", score: 2 },
        { id: "KABS_Bupropion", name: "Bupropion", score: 1 },
        { id: "KABS_Citalopram", name: "Citalopram", score: 1 },
        { id: "KABS_Desvelafaxine", name: "Desvelafaxine", score: 1 },
        { id: "KABS_Escitalopram", name: "Escitalopram", score: 1 },
        { id: "KABS_Fluoxetine", name: "Fluoxetine", score: 1 },
        { id: "KABS_Fluvoxamine", name: "Fluvoxamine", score: 1 },
        { id: "KABS_Mirtazapine", name: "Mirtazapine", score: 1 },
        { id: "KABS_Trazodone", name: "Trazodone", score: 1 },
        { id: "KABS_Venlafaxine", name: "Venlafaxine", score: 1 }
      ]
    },
    {
      name: "Antiepileptic drug",
      koreanName: "항전간제",
      medications: [
        { id: "KABS_Oxcarbazepine", name: "Oxcarbazepine", score: 2 },
        { id: "KABS_Carbamazepine", name: "Carbamazepine", score: 1 }
      ]
    },
    {
      name: "Antihistamines",
      koreanName: "항히스타민제",
      medications: [
        { id: "KABS_Brompheniramine", name: "Brompheniramine", score: 3 },
        { id: "KABS_Carbinoxamine", name: "Carbinoxamine", score: 3 },
        { id: "KABS_Chlorpheniramine", name: "Chlorpheniramine", score: 3 },
        { id: "KABS_Clemastine", name: "Clemastine", score: 3 },
        { id: "KABS_Dexbrompheniramine", name: "Dexbrompheniramine", score: 3 },
        { id: "KABS_Dexchlorpheniramine", name: "Dexchlorpheniramine", score: 3 },
        { id: "KABS_Diphenhydramine", name: "Diphenhydramine", score: 3 },
        { id: "KABS_Doxylamine", name: "Doxylamine", score: 3 },
        { id: "KABS_Homochlorcyclizine", name: "Homochlorcyclizine", score: 3 },
        { id: "KABS_Hydroxyzine", name: "Hydroxyzine", score: 3 },
        { id: "KABS_Mequitazine", name: "Mequitazine", score: 3 },
        { id: "KABS_Pheniramine", name: "Pheniramine", score: 3 },
        { id: "KABS_Piprinhydrinate", name: "Piprinhydrinate", score: 3 },
        { id: "KABS_Pyrilamine", name: "Pyrilamine (Mepiramine)", score: 3 },
        { id: "KABS_Mepiramine", name: "Mepiramine", score: 3 },
        { id: "KABS_Cyproheptadine", name: "Cyproheptadine", score: 2 },
        { id: "KABS_Triprolidine", name: "Triprolidine", score: 2 },
        { id: "KABS_Alimemazine", name: "Alimemazine (Trimeprazine)", score: 1 },
        { id: "KABS_Trimeprazine", name: "Trimeprazine", score: 1 },
        { id: "KABS_Cetirizine", name: "Cetirizine", score: 1 },
        { id: "KABS_Desloratadine", name: "Desloratadine", score: 1 },
        { id: "KABS_Emedastine", name: "Emedastine", score: 1 },
        { id: "KABS_Ketotifen", name: "Ketotifen", score: 1 },
        { id: "KABS_Levocetirizine", name: "Levocetirizine", score: 1 },
        { id: "KABS_Loratadine", name: "Loratadine", score: 1 }
      ]
    },
    {
      name: "Antiparkinson agents",
      koreanName: "항파킨슨제",
      medications: [
        { id: "KABS_Benztropine", name: "Benztropine", score: 3 },
        { id: "KABS_Biperiden", name: "Biperiden", score: 3 },
        { id: "KABS_Procyclidine", name: "Procyclidine", score: 3 },
        { id: "KABS_Trihexyphenidyl", name: "Trihexyphenidyl", score: 3 },
        { id: "KABS_Amantadine", name: "Amantadine", score: 2 }
      ]
    },
    {
      name: "Antipsychotics",
      koreanName: "항정신병제",
      medications: [
        { id: "KABS_Chlorpromazine", name: "Chlorpromazine", score: 3 },
        { id: "KABS_Chlorprothixene", name: "Chlorprothixene", score: 3 },
        { id: "KABS_Clozapine", name: "Clozapine", score: 3 },
        { id: "KABS_Olanzapine", name: "Olanzapine", score: 3 },
        { id: "KABS_Thioridazine", name: "Thioridazine", score: 3 },
        { id: "KABS_Levomepromazine", name: "Levomepromazine (Methotrimeprazine)", score: 2 },
        { id: "KABS_Methotrimeprazine", name: "Methotrimeprazine", score: 2 },
        { id: "KABS_Loxapine", name: "Loxapine", score: 2 },
        { id: "KABS_Molindone", name: "Molindone", score: 2 },
        { id: "KABS_Perphenazine", name: "Perphenazine", score: 2 },
        { id: "KABS_Pimozide", name: "Pimozide", score: 2 },
        { id: "KABS_Quetiapine", name: "Quetiapine", score: 2 },
        { id: "KABS_Zotepine", name: "Zotepine", score: 2 },
        { id: "KABS_Zuclopenthixol", name: "Zuclopenthixol", score: 2 },
        { id: "KABS_Amisulpride", name: "Amisulpride", score: 1 },
        { id: "KABS_Aripiprazole", name: "Aripiprazole", score: 1 },
        { id: "KABS_Blonanserin", name: "Blonanserin", score: 1 },
        { id: "KABS_Bromperidol", name: "Bromperidol", score: 1 },
        { id: "KABS_Flupentixol", name: "Flupentixol", score: 1 },
        { id: "KABS_Haloperidol", name: "Haloperidol", score: 1 },
        { id: "KABS_Paliperidone", name: "Paliperidone", score: 1 },
        { id: "KABS_Risperidone", name: "Risperidone", score: 1 },
        { id: "KABS_Thiothixene", name: "Thiothixene", score: 1 },
        { id: "KABS_Ziprasidone", name: "Ziprasidone", score: 1 }
      ]
    },
    {
      name: "Antivertigo preparations",
      koreanName: "항현훈제",
      medications: [
        { id: "KABS_Dimenhydrinate", name: "Dimenhydrinate", score: 3 },
        { id: "KABS_Meclizine", name: "Meclizine", score: 3 },
        { id: "KABS_Difenidol", name: "Difenidol", score: 2 },
        { id: "KABS_Cinnarizine", name: "Cinnarizine", score: 1 }
      ]
    },
    {
      name: "Anxiolytics, hypnotics and sedatives",
      koreanName: "항불안제, 수면제 및 진정제",
      medications: [
        { id: "KABS_Alprazolam", name: "Alprazolam", score: 1 },
        { id: "KABS_Chlordiazepoxide", name: "Chlordiazepoxide", score: 1 },
        { id: "KABS_Clonazepam", name: "Clonazepam", score: 1 },
        { id: "KABS_Clorazepate", name: "Clorazepate", score: 1 },
        { id: "KABS_Diazepam", name: "Diazepam", score: 1 },
        { id: "KABS_Estazolam", name: "Estazolam", score: 1 },
        { id: "KABS_Flunitrazepam", name: "Flunitrazepam", score: 1 },
        { id: "KABS_Flurazepam", name: "Flurazepam", score: 1 },
        { id: "KABS_Lorazepam", name: "Lorazepam", score: 1 },
        { id: "KABS_Midazolam", name: "Midazolam", score: 1 },
        { id: "KABS_Temazepam", name: "Temazepam", score: 1 },
        { id: "KABS_Triazolam", name: "Triazolam", score: 1 }
      ]
    },
    {
      name: "Cardiovascular drugs",
      koreanName: "심혈관계 약물",
      medications: [
        { id: "KABS_Digoxin", name: "Digoxin", score: 1 },
        { id: "KABS_Furosemide", name: "Furosemide", score: 1 },
        { id: "KABS_Hydralazine", name: "Hydralazine", score: 1 }
      ]
    },
    {
      name: "Drugs for GI conditions and disorders",
      koreanName: "위장관계 약물",
      medications: [
        { id: "KABS_Atropine", name: "Atropine", score: 3 },
        { id: "KABS_Belladone_Alkaloids", name: "Belladone Alkaloids", score: 3 },
        { id: "KABS_Cimetropium", name: "Cimetropium", score: 3 },
        { id: "KABS_Dicyclomine", name: "Dicyclomine (Dicycloverine)", score: 3 },
        { id: "KABS_Dicycloverine", name: "Dicycloverine", score: 3 },
        { id: "KABS_Difemerine", name: "Difemerine", score: 3 },
        { id: "KABS_Hyoscyamine", name: "Hyoscyamine", score: 3 },
        { id: "KABS_Octylonium_Bromide", name: "Octylonium Bromide", score: 3 },
        { id: "KABS_Oxapium_Iodide", name: "Oxapium Iodide (Cyclonium)", score: 3 },
        { id: "KABS_Cyclonium", name: "Cyclonium", score: 3 },
        { id: "KABS_Scopolamine", name: "Scopolamine", score: 3 },
        { id: "KABS_Scopolamine_Butylbromide", name: "Scopolamine Butylbromide", score: 3 },
        { id: "KABS_Scopolia_Extract", name: "Scopolia Extract", score: 3 },
        { id: "KABS_Tiemonium", name: "Tiemonium", score: 3 },
        { id: "KABS_Timepidium", name: "Timepidium", score: 3 },
        { id: "KABS_Tiquizium", name: "Tiquizium", score: 3 },
        { id: "KABS_Valethamate_Bromide", name: "Valethamate Bromide", score: 3 },
        { id: "KABS_Cimetidine", name: "Cimetidine", score: 2 },
        { id: "KABS_Clidinium", name: "Clidinium", score: 2 },
        { id: "KABS_Glycopyrrolate", name: "Glycopyrrolate", score: 2 },
        { id: "KABS_Loperamide", name: "Loperamide", score: 1 },
        { id: "KABS_Mebeverine", name: "Mebeverine", score: 1 },
        { id: "KABS_Ranitidine", name: "Ranitidine", score: 1 },
        { id: "KABS_Trimebutine", name: "Trimebutine", score: 1 }
      ]
    },
    {
      name: "Genitourinary system and sex hormone",
      koreanName: "비뇨생식기계 및 성호르몬 약물",
      medications: [
        { id: "KABS_Fesoterodine", name: "Fesoterodine", score: 3 },
        { id: "KABS_Flavoxate", name: "Flavoxate", score: 3 },
        { id: "KABS_Imidafenaicn", name: "Imidafenaicn", score: 3 },
        { id: "KABS_Oxybutynin", name: "Oxybutynin", score: 3 },
        { id: "KABS_Propiverine", name: "Propiverine", score: 3 },
        { id: "KABS_Solifenacin", name: "Solifenacin", score: 3 },
        { id: "KABS_Tolterodine", name: "Tolterodine", score: 3 },
        { id: "KABS_Trospium", name: "Trospium", score: 3 }
      ]
    },
    {
      name: "Hormonal preparations",
      koreanName: "호르몬 제제",
      medications: [
        { id: "KABS_Hydrocortisone", name: "Hydrocortisone", score: 1 },
        { id: "KABS_Prednisolone", name: "Prednisolone", score: 1 }
      ]
    },
    {
      name: "Musculoskeletal agent",
      koreanName: "근골격계 약물",
      medications: [
        { id: "KABS_Orphenadrine", name: "Orphenadrine", score: 3 },
        { id: "KABS_Pridinol", name: "Pridinol", score: 3 },
        { id: "KABS_Cyclobenzaprine", name: "Cyclobenzaprine", score: 2 },
        { id: "KABS_Tizanidine", name: "Tizanidine", score: 2 },
        { id: "KABS_Baclofen", name: "Baclofen", score: 1 },
        { id: "KABS_Methocarbamol", name: "Methocarbamol", score: 1 }
      ]
    },
    {
      name: "Respiratory agent",
      koreanName: "호흡기계 약물",
      medications: [
        { id: "KABS_Cloperastine", name: "Cloperastine", score: 2 },
        { id: "KABS_Dextromethorphan", name: "Dextromethorphan", score: 1 },
        { id: "KABS_Guaifenesin", name: "Guaifenesin", score: 1 },
        { id: "KABS_Theophylline", name: "Theophylline", score: 1 }
      ]
    }
  ];

  const getScoreClass = (score: number) => {
    return `score-${score}`;
  };

  const getScoreBadgeClass = (score: number) => {
    switch (score) {
      case 1:
        return "score-badge score-1";
      case 2:
        return "score-badge score-2";
      case 3:
        return "score-badge score-3";
      default:
        return "score-badge";
    }
  };

  return (
    <Container>
      <Title>
        항콜린성 약물 점수표
        <br />
        (Anticholinergic Drug Scores)
      </Title>
      
      <Description>
        각 약물의 항콜린성 부작용 가능성을 점수화한 자료로, 고령자 약물 검토 시 누적 항콜린성 부하를 평가하거나, 감량 우선순위 판단에 활용할 수 있습니다.
        <br />
        <strong>점수 체계:</strong> 1점 (경미), 2점 (중등도), 3점 (심각)
      </Description>

      <div className="category-section">
        {categories.map((category) => (
          <div key={category.name} className="category-section">
            <div className="category-title">
              <strong>{category.koreanName} ({category.name})</strong>
            </div>
            <ul className="medication-list">
              {category.medications.map((medication, index) => (
                <li key={`${category.name}-${index}`} id={medication.id} className={`medication-item ${getScoreClass(medication.score)}`}>
                  <span className="medication-name">{medication.name}</span>
                  <span className={getScoreBadgeClass(medication.score)}>
                    {medication.score}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <div style={{ marginTop: "30px", fontSize: "12px", color: "#666" }}>
        <p><strong>사용 지침:</strong></p>
        <ul>
          <li>환자의 모든 약물에 대해 점수를 합산하여 총 항콜린성 부하를 계산합니다.</li>
          <li>총점이 높을수록 항콜린성 부작용 위험이 증가합니다.</li>
          <li>고령자의 경우 특히 주의하여 모니터링해야 합니다.</li>
          <li>점수가 높은 약물부터 우선적으로 감량 또는 대체를 고려합니다.</li>
        </ul>
      </div>
    </Container>
  );
};

export default KABS; 